import Picker from 'vanilla-picker';

// Helper to convert 'R G B A' string (where A is 0-255) to rgba() string
const appColorToRgba = (appColor) => {
  if (!appColor || typeof appColor !== 'string') return 'rgba(0, 0, 0, 1)'; // Default
  const parts = appColor.split(' ').map(Number);
  if (parts.length === 3) {
    return `rgb(${parts[0]}, ${parts[1]}, ${parts[2]})`;
  }
  if (parts.length === 4) {
    // Ensure alpha is between 0 and 1
    const alpha = Math.max(0, Math.min(1, parseFloat((parts[3] / 255).toFixed(2))));
    return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
  }
  return 'rgba(0, 0, 0, 1)'; // Fallback
};

// Helper to convert rgba() or rgb() string to 'R G B A' string (A is 0-255)
const rgbaToAppColor = (rgbaColor) => {
  if (!rgbaColor || typeof rgbaColor !== 'string') return '0 0 0 255';
  const match = rgbaColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (match) {
    const r = parseInt(match[1], 10);
    const g = parseInt(match[2], 10);
    const b = parseInt(match[3], 10);
    // Ensure alpha is between 0 and 255
    const a = match[4] ? Math.max(0, Math.min(255, Math.round(parseFloat(match[4]) * 255))) : 255;
    return `${r} ${g} ${b} ${a}`;
  }
  return '0 0 0 255'; // Fallback
};

const ColorPickerHook = {
  mounted() {
    const actionId = this.el.dataset.actionId;
    const targetInputId = this.el.dataset.targetInput;
    const swatchId = this.el.dataset.swatch;

    let initialAppColor = '0 0 0 255'; // Default
    let parentElement = this.el;
    this.isDirectSwatch = !!actionId;

    if (this.isDirectSwatch) {
      // Direct Swatch Mode
      initialAppColor = this.el.dataset.initialColor || '0 0 0 255';
      this.actionId = actionId;
      // Picker attaches to the swatch itself
    } else if (targetInputId) {
      // Modal Mode
      const targetInput = document.getElementById(targetInputId);
      const swatch = document.getElementById(swatchId);
      if (!targetInput || !swatch) {
        console.error('ColorPickerHook (Modal): Target input or swatch not found.');
        return;
      }
      this.targetInputElement = targetInput;
      this.swatchElement = swatch;
      initialAppColor = targetInput.value || '0 0 0 255';
      // Picker attaches to the trigger button (this.el)
    } else {
      console.error('ColorPickerHook: Invalid configuration. Missing data-action-id or data-target-input.');
      return;
    }

    const initialRgbaColor = appColorToRgba(initialAppColor);

    // Set initial background for the correct element
    if (this.isDirectSwatch) {
       this.el.style.backgroundColor = initialRgbaColor;
    } else {
       this.swatchElement.style.backgroundColor = initialRgbaColor;
    }

    const picker = new Picker({
      parent: parentElement,
      popup: 'right',
      color: initialRgbaColor,
      alpha: true,
      editor: true,
      editorFormat: 'rgba',
      onChange: (color) => {
        // Update the correct swatch visually during changes
        const rgbaString = color.rgbaString;
        if (this.isDirectSwatch) {
          this.el.style.backgroundColor = rgbaString;
        } else {
          this.swatchElement.style.backgroundColor = rgbaString;
        }
        
        // Also update the input value immediately for modal mode
        if (!this.isDirectSwatch && this.targetInputElement) {
          const appColorString = rgbaToAppColor(rgbaString);
          this.targetInputElement.value = appColorString;
          // Don't dispatch any events to avoid interfering with the picker
        }
      },
      onDone: (color) => {
        // Update input/push event only when done
        const rgbaString = color.rgbaString;
        const appColorString = rgbaToAppColor(rgbaString);

        if (this.isDirectSwatch) {
          // Push event to LiveView for direct update
          this.pushEvent("update_action_color", { action_id: this.actionId, color: appColorString });
        } else {
          // Update hidden input and dispatch event for modal form
          this.targetInputElement.value = appColorString;
          this.targetInputElement.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    });

    this.picker = picker;

    // --- External Update Handling (Simplified - might need adjustment based on usage) ---
    // This logic primarily benefits the modal case where the input value might change
    // For direct swatch, the `data-initial-color` might not update without LV replacing the element
    this.handleExternalUpdate = () => {
        let currentAppColor;
        if (this.isDirectSwatch) {
            // Re-read data attribute if the element is potentially replaced by LiveView
            // Note: If only the style is updated, this won't reflect external changes unless data-initial-color is also updated.
            currentAppColor = this.el.dataset.initialColor || '0 0 0 255';
        } else if (this.targetInputElement) {
            currentAppColor = this.targetInputElement.value || '0 0 0 255';
        }

        if (currentAppColor) {
            const newRgbaColor = appColorToRgba(currentAppColor);
            // Update picker only if the color string has actually changed
            if (this.picker && newRgbaColor !== this.picker.color.rgbaString) {
               this.picker.setColor(newRgbaColor, true); // true = silent update
               // Update visual element too
               if (this.isDirectSwatch) {
                   this.el.style.backgroundColor = newRgbaColor;
               } else {
                   this.swatchElement.style.backgroundColor = newRgbaColor;
               }
            }
        }
    }
    this.handleExternalUpdate(); // Initial sync
  },

  updated() {
     // Re-run external update logic if the hook's element was updated by LiveView
     if (this.handleExternalUpdate) {
         this.handleExternalUpdate();
     }
  },

  destroyed() {
    // Ensure proper cleanup regardless of connection state
    if (this.picker) {
      try {
        this.picker.destroy();
      } catch (error) {
        console.warn('Error destroying color picker:', error);
      }
      this.picker = null;
    }
    
    // Clear cached references
    this.targetInputElement = null;
    this.swatchElement = null;
    this.handleExternalUpdate = null;
  }
};

export default ColorPickerHook; 